/**
 * Serve Ticket Network listing images from our S3 copy of the performer/venue image library
 * instead of the per-listing Sharetribe copies the TN sync uploads. Sharetribe bills every
 * distinct image viewed each month, and each TN listing carries its own copies of shared
 * library images, so rendering the shared S3 originals removes nearly all image-CDN usage.
 *
 * Applied to API responses before they enter Redux (see `updatedEntities` in ./data.js), so
 * every component that renders `listing.images` picks it up unchanged.
 */
import { optimizeImageUrl } from './imageOptimizer';
import { isTicketNetworkEventListing } from './listingEventDate';

export const LIBRARY_ATTACHMENTS_KEY = 'ticketNetworkLibraryAttachments';

const S3_BASE = 'https://promotix.s3.us-east-1.amazonaws.com/listing-images';
export const DEFAULT_LISTING_IMAGE_KEY = 'default/PromoTix-Reseller-DefaultImage.jpg';

// Rendered through the WebP optimizer at these sizes (names match Sharetribe variant names).
const OPTIMIZED_VARIANTS = {
  'listing-card': [400, 300],
  'listing-card-2x': [800, 600],
  'scaled-small': [320, 240],
  'scaled-medium': [750, 563],
  'scaled-large': [1024, 768],
  'scaled-xlarge': [1600, 1200],
  'square-small': [240, 240],
  'square-small2x': [480, 480],
  'landscape-crop': [400, 300],
  'landscape-crop2x': [800, 600],
};

// Social cards, JSON-LD, and feeds need JPEG/PNG, so these point at the S3 original.
const ORIGINAL_VARIANTS = ['default', 'facebook', 'twitter'];
const LIBRARY_ORIGINAL_SIZE = [750, 563];
const DEFAULT_ORIGINAL_SIZE = [640, 480];

// Add to any sparse `fields.listing` query so TN listings can be recognized and rewritten.
export const LIBRARY_IMAGE_LISTING_FIELDS = [
  'publicData.ticketNetworkEventListing',
  'publicData.ticketNetworkListingMode',
  'publicData.ticketNetworkEventId',
  `publicData.${LIBRARY_ATTACHMENTS_KEY}`,
];

export const isListingLibraryImagesEnabled = () =>
  process.env.REACT_APP_LISTING_LIBRARY_IMAGES === 'true';

export const libraryImageOriginalUrl = key =>
  `${S3_BASE}/${key
    .split('/')
    .map(encodeURIComponent)
    .join('/')}`;

const libraryKeyPath = key => (key === DEFAULT_LISTING_IMAGE_KEY ? key : `library/${key}`);

// Absolute so consumers outside the browser page (Attendee App, scrapers of the preloaded
// state) can load them; SSR and the client read the same env var, so hydration matches.
const marketplaceOrigin = () => {
  try {
    return new URL(process.env.REACT_APP_MARKETPLACE_ROOT_URL).origin;
  } catch (e) {
    return '';
  }
};

const absoluteSameSiteUrl = url => (url.startsWith('/') ? `${marketplaceOrigin()}${url}` : url);

/** Synthetic image entity with Sharetribe-shaped variants for one library key. */
export const libraryImageEntity = key => {
  const original = libraryImageOriginalUrl(libraryKeyPath(key));
  const variants = {};
  Object.entries(OPTIMIZED_VARIANTS).forEach(([name, [width, height]]) => {
    variants[name] = {
      name,
      width,
      height,
      url: absoluteSameSiteUrl(optimizeImageUrl(original, width)),
    };
  });
  const [width, height] =
    key === DEFAULT_LISTING_IMAGE_KEY ? DEFAULT_ORIGINAL_SIZE : LIBRARY_ORIGINAL_SIZE;
  ORIGINAL_VARIANTS.forEach(name => {
    variants[name] = { name, width, height, url: original };
  });
  return { id: { uuid: `library:${key}` }, type: 'image', attributes: { variants } };
};

/**
 * Library keys to show for a TN listing, in gallery order, or null to keep its Sharetribe images.
 * The TN sync always writes the attachments array (possibly empty) once it has processed a
 * listing, so a missing array means "not processed" or "field not fetched" (e.g. an older
 * catalog snapshot) rather than "no library image".
 */
export const libraryImageKeysForListing = listing => {
  const pd = listing?.attributes?.publicData;
  if (!pd || !isTicketNetworkEventListing(pd) || !Array.isArray(pd[LIBRARY_ATTACHMENTS_KEY])) {
    return null;
  }
  const keys = pd[LIBRARY_ATTACHMENTS_KEY]
    .map(a => (a && typeof a.key === 'string' ? a.key : null))
    .filter(k => k && /^(performers|venues)\/[A-Za-z0-9_-]+\/[^/]+$/.test(k));
  return keys.length ? keys : [DEFAULT_LISTING_IMAGE_KEY];
};

/**
 * Return a copy of a JSON:API-style response (`{ data, included }`) where TN listings reference
 * library image entities instead of their Sharetribe images. No-op when the flag is off.
 */
export const withLibraryImages = apiResponse => {
  if (!isListingLibraryImagesEnabled() || !apiResponse) {
    return apiResponse;
  }
  const { data, included = [] } = apiResponse;
  const isArray = Array.isArray(data);
  const resources = isArray ? data : [data];
  const entitiesByKey = new Map();

  const rewrite = obj => {
    if (!obj || obj.type !== 'listing') {
      return obj;
    }
    const keys = libraryImageKeysForListing(obj);
    if (!keys) {
      return obj;
    }
    keys.forEach(k => {
      if (!entitiesByKey.has(k)) entitiesByKey.set(k, libraryImageEntity(k));
    });
    return {
      ...obj,
      relationships: {
        ...obj.relationships,
        images: { data: keys.map(k => ({ id: { uuid: `library:${k}` }, type: 'image' })) },
      },
    };
  };

  const nextResources = resources.map(rewrite);
  const nextIncluded = included.map(rewrite);
  if (!entitiesByKey.size) {
    return apiResponse;
  }
  return {
    ...apiResponse,
    data: isArray ? nextResources : nextResources[0],
    included: nextIncluded.concat([...entitiesByKey.values()]),
  };
};
